import type { FC } from 'react'
import { cn } from '../utils/cn'
import MdiCalendar from '~icons/mdi/calendar'
import MdiChartBar from '~icons/mdi/chart-bar'
import MdiChevronDown from '~icons/mdi/chevron-down'
import MdiChevronUp from '~icons/mdi/chevron-up'
import MdiChevronDownCircle from '~icons/mdi/chevron-down-circle'
import MdiChevronUpCircle from '~icons/mdi/chevron-up-circle'
import MdiClose from '~icons/mdi/close'
import MdiMenuOpen from '~icons/mdi/menu-open'
import MdiPin from '~icons/mdi/pin'
import MdiPlusCircle from '~icons/mdi/plus-circle'
import MdiDrag from '~icons/mdi/drag'
import MdiBlock from '~icons/mdi/block'
import MidEye from '~icons/mdi/eye'
import MdiAdm from '~icons/mdi/view-split-horizontal'
import MdiCheckOutline from '~icons/mdi/success-circle-outline'
import MdiCheck from '~icons/mdi/check'
import MdiChevronRight from '~icons/mdi/chevron-right'
import MdiCircle from '~icons/mdi/circle'
import MdiCircleOutline from '~icons/mdi/circle-outline'
import MdiDownload from '~icons/mdi/download'
import MdiUpload from '~icons/mdi/upload'
import MdiKeyboardArrowBigUp from '~icons/mdi/keyboard-arrow-up'
import MdiKeyboardArrowBigDown from '~icons/mdi/keyboard-arrow-down'
import MdiOpenInNew from '~icons/mdi/open-in-new'
import MdiRefresh from '~icons/mdi/refresh'
import MdiReportAlert from '~icons/mdi/alert-octagon'
import MdiSearch from '~icons/mdi/search'
import MdiSettings from '~icons/mdi/settings'
import MdiOrganize from '~icons/mdi/unfold-more-horizontal'
import MdiSuccess from '~icons/mdi/success-circle'
import MdiWarning from '~icons/mdi/warning'
import MdiClock from '~icons/mdi/clock-outline'
import MdiAccount from '~icons/mdi/account'
import MdiWrench from '~icons/mdi/wrench'
import PubstackInfo from '~icons/pubstack/info'
import PubstackParenthesis from '~icons/pubstack/parenthesis'
import MdiDelete from '~icons/mdi/delete'
import { Colors } from '../style/colors'

// Mapping des icônes — Ajouter ici pour avoir l'autocomplete
// Voir ADR-0027 pour la convention d'ajout
// Avant d'ajouter une icône custom, vérifie d'abord si un équivalent MDI existe : https://icon-sets.iconify.design/?query=
const icons = {
  // MDI icons
  calendar: MdiCalendar,
  add_circle: MdiPlusCircle,
  drag_handle: MdiDrag,
  chart_bar: MdiChartBar,
  chevron_down: MdiChevronDown,
  chevron_up: MdiChevronUp,
  chevron_down_circle: MdiChevronDownCircle,
  chevron_up_circle: MdiChevronUpCircle,
  close: MdiClose,
  menu_extend: MdiMenuOpen,
  keep: MdiPin,
  block: MdiBlock,
  check_circled: MdiSuccess,
  view: MidEye,
  circle: MdiCircle,
  circle_outline: MdiCircleOutline,
  ad_stack: MdiAdm,
  check_outline: MdiCheckOutline,
  check: MdiCheck,
  chevron_right: MdiChevronRight,
  download: MdiDownload,
  upload: MdiUpload,
  organize_up: MdiKeyboardArrowBigUp,
  organize_down: MdiKeyboardArrowBigDown,
  open_in_new: MdiOpenInNew,
  refresh: MdiRefresh,
  alert: MdiReportAlert,
  search: MdiSearch,
  settings: MdiSettings,
  organize: MdiOrganize,
  warning: MdiWarning,
  clock: MdiClock,
  person: MdiAccount,
  build: MdiWrench,
  trash: MdiDelete,
  // Custom icons
  info: PubstackInfo,
  parenthesis: PubstackParenthesis,
} as const

export type IconName = keyof typeof icons

export const ICON_SIZES = {
  xs: '0.75rem', // 12px
  sm: '1rem', // 16px
  md: '1.25rem', // 20px
  lg: '1.5rem', // 24px
  xl: '2rem', // 32px
} as const

export type IconSize = keyof typeof ICON_SIZES

const SIZE_CLASSES: Record<IconSize, string> = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-7 h-7',
  xl: 'w-8 h-8',
}

export type IconProps = {
  name: IconName
  size?: IconSize
  color?: string
  fill?: string
  className?: string
  style?: React.CSSProperties
  spin?: boolean
  stroke?: string
}

export const Icon: FC<IconProps> = ({ name, size = 'md', color, fill, className, style, spin, stroke }) => {
  const IconComponent = icons[name]

  const dynamicStyle = {
    ...(color !== undefined && { color }),
    ...(fill !== undefined && { '--fill-color': fill }),
    ...(stroke !== undefined && { '--stroke-color': stroke }),
  }

  return (
    <span className={cn('inline-flex items-center justify-center content-center', SIZE_CLASSES[size], spin && 'animate-spin', className)} style={dynamicStyle}>
      <IconComponent color={color} style={style} className="block align-middle" fill={fill ?? Colors.Ink} width="100%" height="100%" />
    </span>
  )
}
